import {
  ChangeEventHandler,
  Dispatch,
  FormEvent,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useGamepadNavigation } from './use-gamepad-navigation';

type LocalSpeechRecognition = SpeechRecognition;

export interface InputSpeechRecognitionOptions {
  inputRef: MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  setInternalValue: Dispatch<SetStateAction<string>>;
  isRecording: boolean;
  setIsRecording: Dispatch<SetStateAction<boolean>>;
  isControlled: boolean;
  currentValue: string;
  value: string | number | readonly string[] | undefined;
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
}

export interface SpeechRecognitionCallbacks {
  handleInput: (e: FormEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  lastInterimRef: MutableRefObject<string>;
}

export function useSpeechRecognition(
  options: InputSpeechRecognitionOptions
): SpeechRecognitionCallbacks {
  const {
    inputRef,
    setInternalValue,
    isRecording,
    setIsRecording,
    isControlled,
    currentValue,
    value,
    onChange,
  } = options;

  const recognitionRef = useRef<LocalSpeechRecognition | null>(null);
  const lastInterimRef = useRef('');

  const updateValue = useCallback(
    (newValue: string) => {
      // console.log('🔄 Updating value:', {
      //     newValue,
      //     isControlled,
      //     currentValue: value,
      //     isRecording
      // })

      if (!isControlled) {
        setInternalValue(newValue);
      }
      if (onChange) {
        const syntheticEvent = {
          target: inputRef.current || { value: newValue },
          currentTarget: inputRef.current || { value: newValue },
          preventDefault: () => {},
          stopPropagation: () => {},
          bubbles: true,
          cancelable: true,
          defaultPrevented: false,
          type: 'change',
          nativeEvent: new Event('change'),
        } as React.ChangeEvent<HTMLTextAreaElement>;

        // Only update the textarea value if we're not recording
        // This prevents the loop of updates during recording
        if (inputRef.current && !isRecording) {
          const originalValue = inputRef.current.value;
          inputRef.current.value = newValue;
          onChange(syntheticEvent);
          if (isControlled) {
            inputRef.current.value = originalValue;
          }
        } else {
          onChange(syntheticEvent);
        }
      }
    },
    [onChange, isControlled, value, isRecording, setInternalValue, inputRef]
  );

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    updateValue(newValue);
  };

  const startRecording = useCallback(() => {
    // Check focus
    if (document.activeElement !== inputRef.current || isRecording) {
      return;
    }

    console.log('Entered via gamepad');

    console.log('🎤 Attempting to start recording...');
    if (isRecording || !recognitionRef.current) {
      console.log('🎤 Cannot start recording:', {
        isRecording,
        hasRecognition: !!recognitionRef.current,
      });
      return;
    }

    try {
      console.log('🎤 Starting recording...');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsRecording(false);
    }
  }, [isRecording, recognitionRef, setIsRecording, inputRef]);

  const stopRecording = useCallback(() => {
    console.log('Entered via gamepad');

    console.log('🎤 Attempting to stop recording...');
    if (!recognitionRef.current) {
      console.log('🎤 No recognition instance to stop');
      setIsRecording(false);
      return;
    }

    try {
      console.log('🎤 Stopping recording...');

      // recognitionRef.current. .abort() // Force stop
      recognitionRef.current.stop();
      console.log('🎤 Aborted.', recognitionRef.current);

      // Immediate cleanup
      setIsRecording(false);
      lastInterimRef.current = '';
    } catch (error) {
      console.error('Failed to stop recording:', error);
      setIsRecording(false);
      lastInterimRef.current = '';
    }
  }, [recognitionRef, setIsRecording]);

  const handleRecognitionResult = useCallback(
    (event: SpeechRecognitionEvent) => {
      let interimTranscript = '';
      let finalTranscript = '';

      // Process all results
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript + ' ';
        }
      }

      // If we have final results, update the content
      if (finalTranscript) {
        const newContent = ((currentValue || '') + ' ' + finalTranscript).trim();
        updateValue(newContent);
      }

      // Update interim
      lastInterimRef.current = interimTranscript.trim();
    },
    [currentValue, updateValue]
  );

  const handleSpeechRecognitionEnd = useCallback(() => {
    // If we have interim results when recording ends, make them final
    if (lastInterimRef.current) {
      const newContent = ((currentValue || '') + ' ' + lastInterimRef.current).trim();
      updateValue(newContent);
    }

    lastInterimRef.current = '';
    setIsRecording(false);
    console.log('🎤 Recording ended');
  }, [currentValue, lastInterimRef, updateValue, setIsRecording]);

  const handleSpeechRecognitionError = useCallback(
    (event: SpeechRecognitionErrorEvent) => {
      if (event.error !== 'no-speech') {
        // Don't log no-speech errors
        // console.error('🎤 Recognition error:', { error: event.error, message: event.message })
      }

      if (event.error === 'no-speech' || event.error === 'aborted') {
        return;
      }

      lastInterimRef.current = '';
      setIsRecording(false);
    },
    [lastInterimRef, setIsRecording]
  );

  useEffect(() => {
    // @ts-expect-error - SpeechRecognition is not in lib.dom.d.ts
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('Speech Recognition API not supported');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      lastInterimRef.current = '';
      setIsRecording(true);
      console.log('🎤 Recording started');
    };

    recognition.onresult = handleRecognitionResult;
    recognition.onend = handleSpeechRecognitionEnd;
    recognition.onerror = handleSpeechRecognitionError;

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error('Error stopping recognition on cleanup:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useGamepadNavigation({
    y: {
      holdable: true,
      pressed: startRecording,
      released: stopRecording,
    },
  });

  return {
    handleInput,
    lastInterimRef,
  };
}
